import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react'
import type { DirectorySignal } from '@/modules/directory-feed/lib/directory-signal'
import {
	type DirectoryFeedHandle,
	type DirectoryFeedState,
	openDirectoryFeed
} from '@/modules/directory-feed/lib/open-directory-feed'
import { clientSessionQueryOptions } from '@/query/query-options'

/**
 * The one owner of the directory channel.
 *
 * **It belongs to the session, so it is mounted in the session's shell.** A
 * subscription opened by a folder page would close on the first navigation and
 * open again on the next: one socket, one handshake and two token mints per
 * click. Here it is opened once when somebody arrives and closed once when
 * they leave.
 *
 * **Context, and not props.** Its consumers are the listings, several routes
 * below, with nothing in between that has any use for a feed — exactly the
 * durable distant consumers `architecture.md` allows context for.
 *
 * **Two contexts became three in MS28, and the third joins them for the same
 * reason the first two are split.** `announceNodeViewed` never changes
 * identity and never needs to: it is a stable bridge to whatever feed happens
 * to be open, exactly like `useDirectorySignals`' listener set. Putting it in
 * `DirectoryFeedStateContext` would re-render every caller of
 * `announceNodeViewed` each time the feed blinked, for a function that reads
 * none of that state — and putting it in `DirectoryFeedSignalsContext` would
 * make "the set of listeners" and "the one function that sends" answer to a
 * reader who asked a different question. A third context is one more line
 * here against a re-render nobody downstream should pay for.
 */
export function DirectoryFeedProvider({ children }: { children: ReactNode }) {
	const { data: session } = useSuspenseQuery(clientSessionQueryOptions())
	const organizationId =
		session.sessionType === 'authenticated' ? session.organization.id : null

	const [status, setStatus] = useState<DirectoryFeedStatus>({
		state: 'connecting',
		recoverable: false
	})
	const listeners = useRef(new Set<DirectorySignalListener>()).current
	const queryClient = useQueryClient()
	const router = useRouter()

	// The open feed's own `rpc`, read by `announceNodeViewed` below rather
	// than captured by it: the feed this effect opens is rebuilt on an
	// organization change, closed on unmount, and briefly absent while the
	// first credentials request is in flight, and a ref is what lets the one
	// stable function reach whichever of those is currently true instead of
	// a snapshot from whenever it was created.
	const feed = useRef<DirectoryFeedHandle | null>(null)

	useEffect(() => {
		if (!organizationId) {
			return
		}

		const opened = openDirectoryFeed({
			onState: (state, recoverable) => setStatus({ state, recoverable }),
			// Delivered out of the live set rather than a captured copy, so a
			// consumer that mounts after the feed is open still receives
			// events — and, more to the point, so mounting one does not tear
			// the connection down and build it again.
			onSignal: (signal) => {
				for (const listener of listeners) {
					listener(signal)
				}
			},
			// The same two steps the SDK takes when a request comes back
			// `session_expired`, and deliberately not a redirect written here:
			// an empty cache makes the `(private)` shell's own guard resolve an
			// anonymous session, and the guard is the one place that decides
			// where a signed-out person goes. The difference is only when it
			// happens — on the socket's close instead of on the next request,
			// which for somebody who is reading rather than clicking is the
			// difference between leaving at once and staying until they touch
			// something.
			onRevoked: async () => {
				queryClient.clear()
				await router.invalidate()
			}
		})
		feed.current = opened

		return () => {
			feed.current = null
			opened.close()
		}
		// The channel is one per person per organization, so an organization
		// change is a different channel and a different token, and the feed
		// has to be rebuilt rather than kept. Today that only ever happens
		// through a fresh sign-in, which remounts this anyway; the dependency
		// is what keeps the code correct if that stops being true.
	}, [organizationId, listeners, queryClient, router])

	// One instance for the life of the provider, the same reasoning
	// `useDirectorySignals` gives for reading its listener through a ref: a
	// fresh closure here on every render would mean every caller that holds
	// this in a dependency array — `use-file-preview.ts`'s transfer effect
	// among them — re-running for a change that is never about the transfer.
	const announceNodeViewed = useRef((nodeId: string) =>
		feed.current?.rpc('directory:node-viewed', { nodeId })
	).current

	return (
		<DirectoryFeedSignalsContext.Provider value={listeners}>
			<DirectoryFeedStateContext.Provider value={status}>
				<DirectoryFeedActionsContext.Provider
					value={announceNodeViewed}
				>
					{children}
				</DirectoryFeedActionsContext.Provider>
			</DirectoryFeedStateContext.Provider>
		</DirectoryFeedSignalsContext.Provider>
	)
}

/**
 * `recoverable` only means something when `state` is `unavailable`: whether
 * the feed had ever reached `live` before it gave up, which is the difference
 * between "reload and it comes back" and "reload hits the same wall".
 */
interface DirectoryFeedStatus {
	state: DirectoryFeedState
	recoverable: boolean
}

/** Whether the list on screen is still being kept true, for a surface that says so. */
export function useDirectoryFeedState() {
	return useFeedContext(DirectoryFeedStateContext)
}

/**
 * Receive every signal the feed delivers, for as long as the caller is mounted.
 *
 * The listener is held in a ref and re-read at delivery, so a caller may pass
 * a fresh closure — over the folder it is showing, over the query client —
 * without resubscribing on every render and without ever going stale. That is
 * the reason this is a hook rather than a set the caller adds itself to.
 *
 * **It has no caller yet, and that is the seam and not an oversight.** What an
 * event means — which folder it belongs to, which query key it invalidates,
 * how a burst of them is collapsed into one re-read — is the file manager's to
 * know, because the keys are the file manager's. T11.3.2 is the consumer, and
 * splitting it out is what keeps the transport ignorant of the cache.
 */
export function useDirectorySignals(listener: DirectorySignalListener) {
	const listeners = useFeedContext(DirectoryFeedSignalsContext)
	const current = useRef(listener)
	current.current = listener

	useEffect(() => {
		const subscriber: DirectorySignalListener = (signal) => {
			current.current(signal)
		}

		listeners.add(subscriber)
		return () => {
			listeners.delete(subscriber)
		}
	}, [listeners])
}

/**
 * Announce that a file's preview has actually shown, over the feed's own
 * channel — `directory:node-viewed`. A no-op, silently, when the feed is not
 * `live`: OPSSTD-8483 is explicit that a view is a suggestion of order and not
 * an audit, so there is nothing to queue and nothing to retry.
 *
 * **The RPC and nothing past it.** What the ack means — that the current
 * listing, if it is sorted by `lastViewed`, should show this file at the head
 * of the viewed ones — is the file manager's to decide, for
 * `useDirectorySignals`' own reason: the query keys are the file manager's,
 * this module owns only the channel.
 */
export function useAnnounceNodeViewed() {
	return useFeedContext(DirectoryFeedActionsContext)
}

type DirectorySignalListener = (signal: DirectorySignal) => void
type AnnounceNodeViewed = (
	nodeId: string
) => ReturnType<DirectoryFeedHandle['rpc']>

const DirectoryFeedStateContext = createContext<
	DirectoryFeedStatus | undefined
>(undefined)

const DirectoryFeedSignalsContext = createContext<
	Set<DirectorySignalListener> | undefined
>(undefined)

const DirectoryFeedActionsContext = createContext<
	AnnounceNodeViewed | undefined
>(undefined)

/**
 * Throws outside the provider rather than answering a plausible default. A
 * listing that silently never receives an event is the defect this feature
 * exists to remove, and an empty listener set would look exactly like one.
 */
function useFeedContext<Value>(context: React.Context<Value | undefined>) {
	const value = useContext(context)

	if (value === undefined) {
		throw new Error(
			'The directory feed is read outside DirectoryFeedProvider, which is mounted in the (private) shell.'
		)
	}

	return value
}
